import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useRelationTypeMutations } from '~/hooks/use-relation-types';
import { relationTypeSchema, EMPTY_RELATION_TYPE, type RelationTypeFormValues } from '~/lib/schemas/relation-type';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: RelationTypeFormValues | null;
}

/** Add / edit a relation type (Τύπος Σχέσης). */
export function RelationTypeEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useRelationTypeMutations();
	const form = useForm<RelationTypeFormValues>({
		resolver: zodResolver(relationTypeSchema) as Resolver<RelationTypeFormValues>,
		defaultValues: initial ?? EMPTY_RELATION_TYPE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_RELATION_TYPE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('catalog.relationTypes.editTitle') : t('catalog.relationTypes.addTitle')}</DialogTitle>
					<DialogDescription>{t('catalog.relationTypes.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('catalog.relationTypes.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<FormField label={t('catalog.relationTypes.columns.code')} error={formState.errors.cd?.message}>
						<Input {...register('cd')} maxLength={100} aria-invalid={Boolean(formState.errors.cd)} />
					</FormField>
					<FormField label={t('catalog.relationTypes.columns.description')} error={formState.errors.desc?.message}>
						<Textarea {...register('desc')} maxLength={200} rows={3} aria-invalid={Boolean(formState.errors.desc)} />
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="rt-iv">{t('catalog.relationTypes.columns.visible')}</Label>
							<Switch id="rt-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<FormField label={t('catalog.relationTypes.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
